import { Link } from 'react-router';
import type {
  AnalyticsAttention,
  AnalyticsBreakdowns,
  AnalyticsLeads,
  AnalyticsMoney,
  AnalyticsUpcoming,
} from '@mys/contracts';
import { REPLY_SLA_MINUTES, sharePercent } from '@mys/contracts/constants';
import { ApiError } from '../lib/api.js';
import { barScale, changePercent, monthLabel, useAnalytics } from '../lib/analytics.js';
import { formatDate, formatDateTime, formatInr } from '../lib/format.js';

/* ============================================================================
   /analytics — what came in, what is outstanding, what is late.
   ----------------------------------------------------------------------------
   Every tile is a link to the screen that does something about it. That is the
   whole design rule: a dashboard of numbers nobody can act on is a dashboard
   nobody opens, and then the one figure that mattered goes unread with the
   rest.

   Everything in rupees here is GROSS — GST included, what the client actually
   pays — and the GST line says how much of this month's takings was never
   ours. MRR is the stated exception: a care plan is sold at ₹999.
   ========================================================================== */

export function AnalyticsPage() {
  const { data, isPending, error } = useAnalytics();

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Analytics</h1>
          <p className="muted">{data ? `As at ${formatDateTime(data.generatedAt)}` : 'Loading…'}</p>
        </div>
      </div>

      {error ? (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load the numbers.'}
        </p>
      ) : null}

      {isPending ? <p className="muted">Loading…</p> : null}

      {data ? (
        <div className="stack">
          {/* Funnel order: who asked, what they paid, the trend, what is
              waiting. Leads come first because every other figure here starts
              as one — and conditionally, because an API 25 minutes older than
              this build does not send the block at all. See AnalyticsResponse. */}
          {data.leads ? <Leads leads={data.leads} /> : null}
          <Money money={data.money} />
          {/* Directly under Money, because its bars add up to the Outstanding
              figure printed there and the two are read together. */}
          {data.upcoming ? <Upcoming upcoming={data.upcoming} /> : null}
          <Trend money={data.money} />
          {/* Conditional for the same reason as the leads block: a 25-minute
              older API does not send it. */}
          {data.breakdowns ? <Breakdowns breakdowns={data.breakdowns} /> : null}
          <Attention attention={data.attention} />
        </div>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */

function Stat({
  to,
  label,
  value,
  note,
}: {
  to: string;
  label: string;
  value: string;
  /* `| undefined` explicitly: `exactOptionalPropertyTypes` is on, so an
     optional key and a key holding undefined are different types, and the
     callers below pass the latter from a conditional. */
  note?: string | undefined;
}) {
  return (
    <Link to={to} className="stat">
      <span className="stat__label">{label}</span>
      <span className="stat__value">{value}</span>
      {note ? <span className="stat__note">{note}</span> : null}
    </Link>
  );
}

/**
 * The published promise, reported on.
 *
 * `/` tells every visitor "2 ghante mein reply". This is the only screen that
 * says whether that is true, which is why the percentage is shown with its
 * DENOMINATOR beside it rather than alone: "92%" invites an argument, "11 of
 * 12" ends one. A dash rather than 0% when nothing is decided yet — a new
 * business reading "0% answered in time" on its first morning would be right
 * to distrust the whole screen.
 */
function Leads({ leads }: { leads: AnalyticsLeads }) {
  const { sla, conversion } = leads;
  const hours = REPLY_SLA_MINUTES / 60;

  return (
    <section className="card">
      <h2 className="section-title">Leads</h2>
      <div className="stats">
        <Stat
          to="/leads"
          label="Today"
          value={String(leads.today)}
          note={`${leads.thisWeek} this week`}
        />
        <Stat
          to="/leads"
          label={`Answered inside ${hours} hours`}
          value={sla.percent === null ? '—' : `${sla.percent}%`}
          note={
            sla.percent === null
              ? 'Nothing decided yet'
              : `${sla.insideSla} of ${sla.decided} leads`
          }
        />
        {/* Zero is worth printing here, unlike on the overview strip: this is
            the screen you come to on purpose, and "none owed" is the answer
            you were looking for. */}
        <Stat
          to="/leads?overdue=true"
          label="Owed a reply now"
          value={String(leads.overdueNow)}
          note={leads.overdueNow > 0 ? 'Past the promise' : 'All answered'}
        />
        <Stat
          to="/clients"
          label="Became clients"
          value={conversion.percent === null ? '—' : `${conversion.percent}%`}
          note={`${conversion.clients} of ${conversion.leads} leads`}
        />
      </div>
      <p className="muted" style={{ margin: '12px 0 0', fontSize: 13 }}>
        {/* What the figure covers, said out loud. The window is floored at the
            day `firstRepliedAt` shipped, so this date is not decoration: for
            the first month it is the reason the denominator is small. */}
        Leads that arrived since {formatDate(sla.from)}. A lead still inside its {hours} hours
        counts neither way, and one won or lost with no reply recorded was never measured.
      </p>
    </section>
  );
}

function Money({ money }: { money: AnalyticsMoney }) {
  const change = changePercent(money.paidThisMonthInr, money.paidLastMonthInr);

  /* Hinglish, and the sign is spelled out rather than drawn with an arrow —
     a glyph that means "good" in one direction is the kind of thing that gets
     read the wrong way round at a glance. */
  const changeNote =
    change === null
      ? `Last month ${formatInr(money.paidLastMonthInr)}`
      : `${change >= 0 ? '+' : ''}${change}% on last month`;

  return (
    <section className="card">
      <h2 className="section-title">Money</h2>
      <div className="stats">
        <Stat
          to="/invoices?status=paid"
          label="Received this month"
          value={formatInr(money.paidThisMonthInr)}
          note={changeNote}
        />
        <Stat
          to="/invoices?status=due"
          label="Outstanding"
          value={formatInr(money.outstandingInr)}
          note={`${money.dueInvoices} invoice`}
        />
        <Stat
          to="/care-plans"
          label="Care plan MRR"
          value={formatInr(money.mrrInr)}
          note="Active plans only"
        />
      </div>
      {/* Only when money actually came in. In a quiet month the line read
          "₹0 of ₹0 is GST — that is not yours to keep", which tells
          the owner to remit nothing and reads like a bug. Caught by looking at
          the screenshot, not by a test: every assertion about this line was
          written against a month that had income. */}
      {money.paidThisMonthInr > 0 ? (
        <p className="muted" style={{ margin: '12px 0 0', fontSize: 13 }}>
          {/* Said plainly, because a business that reads its collections as
              income finds out the difference at filing time. */}
          {formatInr(money.gstThisMonthInr)} of this month's {formatInr(money.paidThisMonthInr)} is
          GST — that is not yours to keep, it has to be remitted.
        </p>
      ) : null}
    </section>
  );
}

/**
 * One bar list, drawn once and used three times.
 *
 * Hand-drawn, and that is not stubbornness: a charting library is about 10 KB
 * gzipped against a budget with roughly three to spare, and this is four
 * elements and a percentage width. The track is presentational and the figure
 * beside it is the accessible value, so a screen reader gets the number rather
 * than a percentage of a bar it cannot see.
 */
function Bars({ rows }: { rows: { key: string; label: string; value: number; text: string }[] }) {
  /* `barScale` rather than a peak computed here: that rule is tested, and the
     first draft of this component quietly owned a second copy of it. */
  const scale = barScale(rows.map((row) => row.value));

  return (
    <ol className="bars">
      {rows.map((row, index) => (
        <li key={row.key} className="bars__row">
          <span className="bars__label">{row.label}</span>
          <span className="bars__track" aria-hidden="true">
            <span
              className="bars__fill"
              style={{ width: `${Math.round((scale[index] ?? 0) * 100)}%` }}
            />
          </span>
          <span className="bars__value">{row.text}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * The only part of this screen that looks forward.
 *
 * Both halves are cash. A care plan renewing next week is revenue that arrives
 * on its own; an advance that was never collected is work that should not have
 * started. The due bars carry `care` as well, even though the roadmap asked
 * only for advances and finals — parts printed under a total have to add up to
 * it, or the reader subtracts on paper and finds a gap nobody explained.
 */
function Upcoming({ upcoming }: { upcoming: AnalyticsUpcoming }) {
  const { renewals, dueByType } = upcoming;
  if (renewals.count === 0 && dueByType.length === 0) return null;

  return (
    <section className="card">
      <h2 className="section-title">What is coming</h2>

      {renewals.count > 0 ? (
        <div className="stats" style={{ marginBottom: dueByType.length > 0 ? 16 : 0 }}>
          <Stat
            to="/care-plans"
            label={`Renewing in ${upcoming.renewalDays} days`}
            value={String(renewals.count)}
            note={`${formatInr(renewals.monthlyInr)} a month`}
          />
        </div>
      ) : null}

      {dueByType.length > 0 ? (
        <>
          <h3 className="section-title" style={{ fontSize: 15 }}>
            Owed, by stage
          </h3>
          <Bars
            rows={dueByType.map((row) => ({
              key: row.label,
              label: row.label,
              value: row.valueInr ?? 0,
              text: `${formatInr(row.valueInr ?? 0)} · ${row.count}`,
            }))}
          />
          <p className="muted" style={{ margin: '12px 0 0', fontSize: 13 }}>
            An advance still owed is work that should not have started. These add up to the
            outstanding total above.
          </p>
        </>
      ) : null}
    </section>
  );
}

function Trend({ money }: { money: AnalyticsMoney }) {
  const empty = money.months.every((month) => month.paidInr === 0);

  return (
    <section className="card">
      {/* The LENGTH the API sent, not a hard-coded twelve: an API older than
          this build sends six, and a heading that lies about the window is
          worse than a shorter chart. */}
      <h2 className="section-title">Last {money.months.length} months</h2>

      {empty ? (
        <p className="muted" style={{ margin: 0 }}>
          No invoice has been paid yet. The trend appears here after the first payment.
        </p>
      ) : (
        <Bars
          rows={money.months.map((month) => ({
            key: month.month,
            label: monthLabel(month.month),
            value: month.paidInr,
            text: formatInr(month.paidInr),
          }))}
        />
      )}
    </section>
  );
}

/**
 * Where the work came from and what it sold for.
 *
 * Two questions the owner acts on: which channel is worth the next pamphlet
 * run, and which plan is worth selling. Sources carry no money on purpose —
 * a source introduces, it does not earn — so they are counted, while plans
 * are ranked by the rupees actually agreed.
 */
function Breakdowns({ breakdowns }: { breakdowns: AnalyticsBreakdowns }) {
  const { bySource, byPlan } = breakdowns;
  if (bySource.length === 0 && byPlan.length === 0) return null;

  const split = breakdowns.oneTimeInr + breakdowns.recurringInr;

  return (
    <section className="card">
      <h2 className="section-title">Where it comes from</h2>

      {split > 0 ? (
        <div className="stats" style={{ marginBottom: 16 }}>
          <Stat
            to="/invoices?status=paid"
            label="One-time"
            value={formatInr(breakdowns.oneTimeInr)}
            note="Builds — advance and final"
          />
          <Stat
            to="/care-plans"
            label="Recurring"
            value={formatInr(breakdowns.recurringInr)}
            note={`${sharePercent(breakdowns.recurringInr, split) ?? 0}% of the year`}
          />
        </div>
      ) : null}

      {bySource.length > 0 ? (
        <>
          <h3 className="section-title" style={{ fontSize: 15 }}>
            Leads by source
          </h3>
          <Bars
            rows={bySource.map((row) => ({
              key: row.label,
              label: row.label,
              value: row.count,
              text: String(row.count),
            }))}
          />
        </>
      ) : null}

      {byPlan.length > 0 ? (
        <>
          <h3 className="section-title" style={{ fontSize: 15, marginTop: 16 }}>
            Sold by plan
          </h3>
          <Bars
            rows={byPlan.map((row) => ({
              key: row.label,
              label: row.label,
              value: row.valueInr ?? 0,
              /* The count rides along with the money: "₹59,997" alone cannot
                 tell you whether that was three sales or thirty. */
              text: `${formatInr(row.valueInr ?? 0)} · ${row.count}`,
            }))}
          />
        </>
      ) : null}

      <p className="muted" style={{ margin: '12px 0 0', fontSize: 13 }}>
        Since {formatDate(breakdowns.from)}. Plans are ranked by what was agreed, which is not the
        same as what has been paid — that is the trend above.
      </p>
    </section>
  );
}

function Attention({ attention }: { attention: AnalyticsAttention }) {
  const quiet =
    attention.lateProjects === 0 &&
    attention.openEditRequests === 0 &&
    attention.openLeads === 0 &&
    attention.pendingTestimonials === 0;

  return (
    <section className="card">
      <h2 className="section-title">Needs attention</h2>

      {quiet ? (
        <p className="muted" style={{ margin: 0 }}>
          Nothing is pending. All clear.
        </p>
      ) : (
        <div className="stats">
          <Stat
            to="/projects?overdue=true"
            label="Late projects"
            value={String(attention.lateProjects)}
            note={attention.lateProjects > 0 ? 'The refund clause applies' : undefined}
          />
          <Stat
            to="/edit-requests"
            label="Open change requests"
            value={String(attention.openEditRequests)}
          />
          <Stat to="/leads" label="Open leads" value={String(attention.openLeads)} />
          <Stat
            to="/testimonials"
            label="Testimonials pending"
            value={String(attention.pendingTestimonials)}
          />
        </div>
      )}
    </section>
  );
}
